import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  type AISidekickUiState,
  DOCK_STATE,
  type DockState,
  type StateWithSidekick,
} from './types';

const initialState: AISidekickUiState = {
  isPanelOpen: false,
  dockState: DOCK_STATE.MINIMIZED,
  selectedChipId: null,
  focusToken: 0,
  isResetConfirmOpen: false,
  isResetConfirmLoading: false,
};

/**
 * Single source of truth for the "panel-closed" mutation: panel goes
 * to closed, the dock returns to its minimized resting size, any
 * selected chip is cleared, and any open reset prompt (including its
 * in-flight loading state) is dismissed so a re-opened panel never
 * displays a stale "Selected" pill or stranded confirmation card from
 * a prior session. Both `closePanel` (explicit close) and
 * `togglePanel` (open→closed branch) route through this helper so the
 * chip-clear / prompt-clear contract cannot drift across reducers.
 * The function mutates the Immer draft in place — the draft is the
 * same object reference passed across the call boundary, which is the
 * RTK-recommended pattern for sharing mutation logic between reducers.
 */
function closePanelMutation(state: AISidekickUiState): void {
  state.isPanelOpen = false;
  state.dockState = DOCK_STATE.MINIMIZED;
  state.selectedChipId = null;
  state.isResetConfirmOpen = false;
  state.isResetConfirmLoading = false;
}

/**
 * Counterpart to {@link closePanelMutation}. The dock and the drawer
 * both anchor to the bottom-right of the viewport, so the dock is
 * collapsed as the drawer opens; closing the drawer then reveals the
 * bar rather than a card the user never asked to reopen. Shared by
 * `openPanel` and the closed→open branch of `togglePanel`.
 */
function openPanelMutation(state: AISidekickUiState): void {
  state.isPanelOpen = true;
  state.dockState = DOCK_STATE.MINIMIZED;
}

/**
 * Redux slice for the AI Sidekick UI chrome.
 *
 * Owns only what the panel frame, dock, and entry card need to
 * render: panel open/closed state, the dock's resting size, and the
 * currently selected entry-card chip. Chat
 * history, streaming, tool activity, feedback, and chat errors live in
 * the sibling chat slice exported from the same state module
 * (`./aiSidekickChatSlice.ts`), so surfaces that only need the chrome
 * (e.g. preview pages) can mount this reducer independently.
 *
 * Chip-clear / reset-prompt side effects (centralized in
 * `closePanelMutation`):
 * - `closePanel` and `togglePanel` (open→closed branch) clear
 *   `selectedChipId` and `isResetConfirmOpen` via the shared mutator.
 * - `resetUiState` clears `selectedChipId`, `focusToken`, and
 *   `isResetConfirmOpen` without touching `isPanelOpen`. Dispatched at
 *   the tail of the in-chat reset flow alongside `resetChat()` from
 *   the chat slice so the chrome lands in a clean state.
 */
export const aiSidekickSlice = createSlice({
  name: 'aiSidekick',
  initialState,
  reducers: {
    openPanel: openPanelMutation,
    closePanel: closePanelMutation,
    togglePanel: (state) => {
      if (state.isPanelOpen) {
        closePanelMutation(state);
      } else {
        openPanelMutation(state);
      }
    },
    /** Collapses the dock to its bar. No effect on the drawer. */
    minimizeDock: (state) => {
      state.dockState = DOCK_STATE.MINIMIZED;
    },
    /** Grows the dock to the suggestion card. No effect on the drawer. */
    expandDock: (state) => {
      state.dockState = DOCK_STATE.EXPANDED;
    },
    /** Flips the dock between its bar and card sizes. */
    toggleDock: (state) => {
      state.dockState =
        state.dockState === DOCK_STATE.EXPANDED
          ? DOCK_STATE.MINIMIZED
          : DOCK_STATE.EXPANDED;
    },
    /**
     * Records the chip id the user clicked on the entry card. Use
     * `null` to clear the selection without closing the panel. Does
     * not mutate `isPanelOpen` — the entry card dispatches
     * `openPanel()` alongside this action.
     */
    selectChip: (state, action: PayloadAction<string | null>) => {
      state.selectedChipId = action.payload;
    },
    /**
     * Resets the UI-only state of the Sidekick chrome
     * (`selectedChipId`, `focusToken`, `isResetConfirmOpen`,
     * `isResetConfirmLoading`) without touching `isPanelOpen`.
     * Dispatched at the tail of the reset flow so the cleared chat
     * returns to a clean chrome state.
     */
    resetUiState: (state) => {
      state.selectedChipId = null;
      state.focusToken = 0;
      state.isResetConfirmOpen = false;
      state.isResetConfirmLoading = false;
    },
    /**
     * Increments `focusToken`, signalling subscribers (the input bar)
     * to take focus. Use this when an action elsewhere in the panel
     * should hand focus back to the input — for example clicking
     * "Ask something else" in the panel zero-state.
     *
     * Each dispatch produces a unique token even when the value would
     * otherwise be unchanged, so subscribers can `useEffect` on the
     * token without missing repeated requests.
     */
    requestInputBarFocus: (state) => {
      state.focusToken += 1;
    },
    /** Opens the in-chat "Start a new chat?" confirmation prompt. */
    openResetConfirm: (state) => {
      state.isResetConfirmOpen = true;
      state.isResetConfirmLoading = false;
    },
    /**
     * Closes the in-chat reset prompt and clears the in-flight
     * loading flag (cleared so a future re-open lands in a fresh
     * default state).
     */
    closeResetConfirm: (state) => {
      state.isResetConfirmOpen = false;
      state.isResetConfirmLoading = false;
    },
    /**
     * Marks the prompt as in the "Continue" loading window between
     * the user's click and the reset dispatch. While `true`, the chat
     * shell suppresses its auto-dismiss-on-typing behavior so a
     * keystroke during the brief mock delay cannot unmount the prompt
     * and cancel the in-flight reset.
     */
    beginResetConfirm: (state) => {
      state.isResetConfirmLoading = true;
    },
  },
});

export const {
  openPanel,
  closePanel,
  togglePanel,
  minimizeDock,
  expandDock,
  toggleDock,
  selectChip,
  resetUiState,
  requestInputBarFocus,
  openResetConfirm,
  closeResetConfirm,
  beginResetConfirm,
} = aiSidekickSlice.actions;

/** Selects whether the Sidekick drawer is currently open. */
export const selectIsPanelOpen = (state: StateWithSidekick): boolean =>
  state.aiSidekick.isPanelOpen;

/** Selects the docked entry point's resting size. */
export const selectDockState = (state: StateWithSidekick): DockState =>
  state.aiSidekick.dockState;

/**
 * Selects the id of the entry-card chip the user most recently picked,
 * or `null` when no chip is selected.
 */
export const selectSelectedChipId = (state: StateWithSidekick): string | null =>
  state.aiSidekick.selectedChipId;

/**
 * Selects the input-bar focus token. Consumers subscribe via this
 * selector and run a focus effect every time the value changes —
 * each increment is an independent request to focus the input.
 */
export const selectFocusToken = (state: StateWithSidekick): number =>
  state.aiSidekick.focusToken;

/** Selects whether the in-chat reset confirmation prompt is visible. */
export const selectIsResetConfirmOpen = (state: StateWithSidekick): boolean =>
  state.aiSidekick.isResetConfirmOpen;

/**
 * Selects whether the prompt is mid-confirmation (between Continue
 * click and reset dispatch). Used by the chat shell to suppress
 * auto-dismiss-on-typing while a reset is in flight.
 */
export const selectIsResetConfirmLoading = (
  state: StateWithSidekick
): boolean => state.aiSidekick.isResetConfirmLoading;

export default aiSidekickSlice.reducer;
